/* eslint @typescript-eslint/no-unused-vars: 0 */
import React, { Component } from 'react';
import { createStyles, StyledComponentProps, withStyles } from '@material-ui/core';
import { List } from 'immutable';
import { FormattedMessage } from 'react-intl';
import { Connect } from 'common/decorators';
import TableSection, {
  ITableHeaderColumn,
  ITableSectionColumn,
} from 'modules/reports/components/TableSection/TableSection';
import {
  selectTaxSummary,
  selectTaxSummaryLoading,
} from 'modules/reports/state/salesPOS/selectors';
import { fetchTaxSummary } from 'modules/reports/state/salesPOS/actions';
import { DEFAULT_TABLE_PARAMS } from 'common/constants/table';
import { ITaxSummaryItem, ITaxSummaryItemImt } from 'modules/reports/interfaces/salesPOS';
import { DispatchFunction } from 'common/state/interfaces';
import { ITableParams, IFooterCell, Order } from 'common/interfaces/table';
import { formatPrice } from 'common/utils';
import inputLabels from 'common/messages/inputLabels';
import messages from 'modules/reports/messages/messages';
import { selectRoot } from 'common/hooks/useRootSelector';

const styles = () => createStyles({});

interface ICashSummaryProps extends StyledComponentProps {
  mainTableParams: ITableParams;
  taxSummary?: List<ITaxSummaryItemImt>;
  taxSummaryLoading?: boolean;

  fetchTaxSummaryData?: (params?) => void;
}

@(withStyles(styles) as any)
@(Connect({
  mapStateToProps: () => ({
    taxSummary: selectRoot(selectTaxSummary()),
    taxSummaryLoading: selectRoot(selectTaxSummaryLoading()),
  }),
  mapDispatchToProps: (dispatch: DispatchFunction) => ({
    fetchTaxSummaryData: (requestOptions?) => dispatch(fetchTaxSummary(requestOptions)),
  }),
}) as any)
class TaxSummary extends Component<ICashSummaryProps, ITableParams<ITaxSummaryItem>> {
  private readonly columnSettings: ITableSectionColumn[];

  private readonly headerSettings: ITableHeaderColumn<ITaxSummaryItem>[];

  constructor(props: ICashSummaryProps) {
    super(props);

    this.state = {
      page: 0,
      perPage: 5,
      orderBy: 'netSales',
      order: 'desc',
      filters: [],
    };

    this.columnSettings = [
      {
        name: 'taxRate',
        label: <FormattedMessage {...inputLabels.taxRate} />,
      },
      {
        name: 'amount',
        label: <FormattedMessage {...inputLabels.amount} />,
        align: 'right',
      },
      {
        name: 'netSales',
        label: <FormattedMessage {...inputLabels.netSales} />,
        align: 'right',
        transform: value => formatPrice(value),
      },
    ];

    this.headerSettings = [
      {
        name: 'taxRate',
        label: <FormattedMessage {...inputLabels.taxRate} />,
        sort: true,
      },
      {
        name: 'amount',
        label: <FormattedMessage {...messages.qty} />,
        sort: true,
        align: 'right',
      },
      {
        name: 'netSales',
        label: <FormattedMessage {...inputLabels.netSales} />,
        align: 'right',
        sort: true,
      },
    ];
  }

  componentDidMount(): void {
    const { fetchTaxSummaryData, mainTableParams } = this.props;
    const { page, perPage, orderBy, order } = this.state;

    this.setState({ filters: mainTableParams.filters });

    if (fetchTaxSummaryData) {
      fetchTaxSummaryData({
        ...DEFAULT_TABLE_PARAMS,
        filters: mainTableParams.filters,
        page,
        perPage,
        orderBy,
        order,
      });
    }
  }

  componentDidUpdate(): void {
    const { mainTableParams, fetchTaxSummaryData } = this.props;
    const { page, perPage, orderBy, order, filters } = this.state;

    if (JSON.stringify(filters) !== JSON.stringify(mainTableParams.filters)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ filters: mainTableParams.filters });

      if (fetchTaxSummaryData) {
        fetchTaxSummaryData({
          ...DEFAULT_TABLE_PARAMS,
          filters: mainTableParams.filters,
          page,
          perPage,
          orderBy,
          order,
        });
      }
    }
  }

  handleChangeTableProps = (page: number, orderBy, order: Order): void => {
    this.updateTableParams({ ...this.state, page, orderBy, order });
  };

  updateTableParams = (tableParams: ITableParams<ITaxSummaryItem>): void => {
    this.setState(tableParams);
    const { fetchTaxSummaryData } = this.props;

    if (fetchTaxSummaryData) {
      fetchTaxSummaryData(tableParams);
    }
  };

  createTableFooter = (): IFooterCell[] => {
    const { taxSummary } = this.props;
    const netSalesAmount = taxSummary?.toJS().reduce((acc, { netSales }) => acc + netSales, 0);

    return [
      {
        id: 'title',
        label: <FormattedMessage {...inputLabels.totalCash} />,
        variant: 'h5',
      },
      {
        id: 'amount',
        label: '',
      },
      {
        id: 'netSales',
        label: formatPrice(netSalesAmount),
        variant: 'h5',
        align: 'right',
      },
    ];
  };

  render(): JSX.Element {
    const { taxSummary, taxSummaryLoading } = this.props;
    const { page, perPage, orderBy, order } = this.state;

    return (
      <TableSection<ITaxSummaryItem, ITaxSummaryItemImt>
        id="cash-summary-section"
        title={<FormattedMessage {...messages.taxSummaryTitle} />}
        columns={this.columnSettings}
        headerColumns={this.headerSettings}
        data={taxSummary as any} // TODO - PRM-3575 need types
        dataLoading={!!taxSummaryLoading}
        page={page}
        total={taxSummary?.size || 25}
        order={order}
        orderBy={orderBy}
        perPage={perPage}
        hidePagination
        footerOptions={{ sticky: true, cells: this.createTableFooter() }}
        onSettingsChanged={this.handleChangeTableProps}
      />
    );
  }
}

export default TaxSummary;
